import AppointmentPhase from "components/Atoms/AppointmentPhase";
import { DateTimePicker } from "components/DatePicker/DateTimePicker";
import { AppointmentPhaseDropdown } from "components/dropdowns/reactselect/AppointmentPhaseDropdown";
import TrainTravelClassDropdown from "components/dropdowns/reactselect/TrainTravelClassDropdown";
import FileList from "components/FileUpload/filelist";
import { ManageTrainPassengerFilesModal } from "components/FileUpload/ManageMultipleFiles/ManageTrainFilesModal";
import { emptySubmitChangeFunc } from "components/MainSectionTemplate/emptySubmitChangeFunc";
import { RenderEditOptions, Section } from "components/MainSectionTemplate/types";
import ArtistModalPicker from "components/Picker/ArtistPicker/ArtistModalPicker";
import { ContactMultiModalPicker } from "components/Picker/ContactPicker/ContactMultiModalPicker";
import { CompanySidePanel } from "components/SidePanel/Company/CompanySidePanel";
import { SidePanelContactList } from "components/SidePanel/contact/SidePanelContactList";
import SmallDetails from "components/SmallDetails/SmallDetails";
import { GooglePlaceInput } from "components/ValidatedFields/GooglePlaceInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import { FormatArrivalTime } from "flights/lookup/FlightSearchResult";
import moment from "moment";
import React from "react";
import { formatTime, formatWrittenDayMonthYear } from "utilities";
import { TrainFileUpload } from "components/FileUpload/FileUploadWithValue";
import OptionalTextAreaInput, { OptionalTextAreaDetails } from "components/ValidatedFields/OptionalTextAreaInput";
import { GooglePlaceState } from "shared/utils/fieldsHelper/groundFields";
import { TrainFieldMetaData, TrainFormState } from "shared/utils/fieldsHelper/trainFields";

export const trainSections = (
  state: TrainFormState,
  fieldMetaData: TrainFieldMetaData,
  onFilesChanged: () => Promise<void>,
  getUpdateTrainDetails: (trainInfo: TrainFormState) => server.dto.UpdateTrainInstruction,
): Array<Section> => [{
  key: "train",
  type: "regular",
  title: RESX.Train.resxTrain,
  fields: [{
    ...fieldMetaData.artist.fields(),
    key: "artist",
    label: site.currentEnvironment.functionGroups.artist.singular,
    render: () => <CompanySidePanel type="artist"
      companyId={state.artist.artistId}
      trigger={<a>{state.artist.name}</a>}
      fallbackIfNoPermission={<>{state.artist.name}</>}
    />,
    inPageEdit: {
      hoverEdit: true,
      render: ({ onChange, cancel, value }) => <div className="main-section-template-fields-padding">
        <ArtistModalPicker
          inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
          securityScope="limitByUserAccess"
          canCreateNew={false}
        />
      </div>,
      fullWidth: false,
      type: "custom",
      initialValue: state.artist,
      submitChange: async (artist: server.dto.ArtistSmall) => window.CoreApi.Train.UpdateTrain(
        getUpdateTrainDetails({ ...state, artist }),
      ),
    },
  }, {
    ...fieldMetaData.travellers.fields(),
    key: "travellers",
    label: RESX.Flight.Travellers,
    render: () => <SidePanelContactList contacts={state.travellers} />,
    inPageEdit: {
      fullWidth: true,
      render: ({ value, onChange, cancel }) => <div className="main-section-template-fields-padding">
        <ContactMultiModalPicker
          inPageEditing={{ cancel }}
          value={value}
          onChange={contacts => onChange(contacts, true)}
          canCreateNew
          required={false}
          validatorKey="contacts"
        />
      </div>,
      initialValue: state.travellers,
      type: "custom",
      submitChange: (travellers: server.dto.ContactSmallV2[]) => window.CoreApi.Train.UpdateTrain({
        ...getUpdateTrainDetails({ ...state, travellers }),
      }),
    }
  }, {
    ...fieldMetaData.phase.fields(),
    key: "status",
    label: RESX.Status.resxStatus,
    render: () => <AppointmentPhase value={state.phase.value} displayLabel />,
    inPageEdit: {
      render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.AppointmentPhase>) => <AppointmentPhaseDropdown
        value={value}
        onChange={(appointmentPhase) => {
          onChange(appointmentPhase, true);
        }}
        onEsc={() => cancel()}
        config={{
          focusOnMount: true,
          openMenuOnFocus: true,
        }}
      />,
      fullWidth: true,
      type: "custom",
      initialValue: state.phase,
      submitChange: (phase: server.dto.AppointmentPhase) => window.CoreApi.Train.UpdateTrain({
        ...getUpdateTrainDetails({ ...state, phase }),
      }),
    },
  }, {
    ...fieldMetaData.bookingReference.fields(),
    key: "bookingReference",
    label: RESX.Flight.BookingCode,
    render: () => <div>{state.bookingReference}</div>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
        focusOnMount
        maxLength={serverReference.constants.train.maxBookingCodeLength}
        value={value}
        onChange={(v) => { onChange(v); }}
        onEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: true,
      type: "confirmOrCancelButton",
      initialValue: state.bookingReference ?? "",
      submitChange: (bookingReference: string) => window.CoreApi.Train.UpdateTrain({
        ...getUpdateTrainDetails({ ...state, bookingReference }),
      }),
    }
  }, {
    ...fieldMetaData.travelAgency.fields(),
    key: "travelAgency",
    label: RESX.Flight.TravelAgency,
    render: () => <div>{state.travelAgency}</div>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
        focusOnMount
        maxLength={serverReference.constants.train.maxTravelAgencyLength}
        value={value}
        onChange={(v) => { onChange(v); }}
        onEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: true,
      type: "confirmOrCancelButton",
      initialValue: state.travelAgency ?? "",
      submitChange: (travelAgency: string) => window.CoreApi.Train.UpdateTrain({
        ...getUpdateTrainDetails({ ...state, travelAgency }),
      }),
    }
  }, {
    ...fieldMetaData.notes.fields(),
    key: "notes",
    label: RESX.Note.Notes,
    render: () => <OptionalTextAreaDetails>{state.notes}</OptionalTextAreaDetails>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
        focusOnMount
        maxLength={serverReference.constants.train.maxNotesLength}
        rows={3}
        value={value}
        onChange={(v) => { onChange(v); }}
        onCtrlEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: true,
      type: "confirmOrCancelButton",
      initialValue: state.notes ?? "",
      submitChange: (notes: string) => window.CoreApi.Train.UpdateTrain({
        ...getUpdateTrainDetails({ ...state, notes }),
      }),
    },
  }, {
    ...fieldMetaData.trainNumber.fields(),
    key: "trainNumber",
    label: RESX.Train.TrainNumber,
    render: () => <div>{state.trainNumber}</div>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
        focusOnMount
        width={100}
        maxLength={serverReference.constants.train.maxTrainNumberLength}
        value={value}
        onChange={(v) => { onChange(v); }}
        onEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: false,
      type: "confirmOrCancelButton",
      initialValue: state.trainNumber ?? "",
      submitChange: (trainNumber: string) => window.CoreApi.Train.UpdateTrain({
        ...getUpdateTrainDetails({ ...state, trainNumber }),
      }),
    },
  }, {
    ...fieldMetaData.carriage.fields(),
    key: "carriage",
    label: RESX.Train.Carriage,
    render: () => <div>{state.carriage}</div>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
        focusOnMount
        width={100}
        maxLength={serverReference.constants.train.maxCarriageLength}
        value={value}
        onChange={(v) => { onChange(v); }}
        onEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: false,
      type: "confirmOrCancelButton",
      initialValue: state.carriage ?? "",
      submitChange: (carriage: string) => window.CoreApi.Train.UpdateTrain({
        ...getUpdateTrainDetails({ ...state, carriage }),
      }),
    }
  }, {
    ...fieldMetaData.seats.fields(),
    key: "seats",
    label: RESX.Flight.Seats,
    render: () => <div>{state.seats}</div>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
        focusOnMount
        width={200}
        maxLength={serverReference.constants.train.maxSeatsLength}
        value={value}
        onChange={(v) => { onChange(v); }}
        onEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: true,
      type: "confirmOrCancelButton",
      initialValue: state.seats ?? "",
      submitChange: (seats: string) => window.CoreApi.Train.UpdateTrain({
        ...getUpdateTrainDetails({ ...state, seats }),
      }),
    },
  }, {
    ...fieldMetaData.travelClass.fields(),
    key: "travelClass",
    label: RESX.Flight.TravelClass,
    render: () => <SmallDetails
      label={state.travelClass.text}
    />,
    inPageEdit: {
      render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.TrainTravelClass>) => <TrainTravelClassDropdown
        value={value}
        onChange={(travelClass) => {
          onChange(travelClass, true);
        }}
        onEsc={() => cancel()}
        config={{
          focusOnMount: true,
          isSearchable: true,
          openMenuOnFocus: true,
        }}
      />,
      fullWidth: true,
      type: "custom",
      initialValue: state.travelClass,
      submitChange: (travelClass: server.dto.TrainTravelClass) => window.CoreApi.Train.UpdateTrain(
        getUpdateTrainDetails({ ...state, travelClass }),
      ),
    }
  }]
}, {
  key: "departureArrival",
  title: "",
  hideTitle: true,
  type: "regular",
  fields: [{
    key: "departureArrivalLabel",
    label: "",
    left: {
      key: "departureLabel",
      render: () => <h3>{RESX.Flight.Departure}</h3>
    },
    right: {
      key: "arrivalLabel",
      render: () => <h3>{RESX.Flight.Arrival}</h3>
    }
  }, {
    key: "date",
    label: RESX.DateTime.lblDate,
    left: {
      key: "departureLocal",
      render: () => <div className="flex-column">
        <div>{formatTime(state.departureLocal)}</div>
        <div>{formatWrittenDayMonthYear(state.departureLocal)}</div>
      </div>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<moment.Moment>) => <DateTimePicker
          value={value}
          onChange={v => onChange(v)}
          validatorKey="departureDate"
          minDate={moment(serverReference.constants.appointment.minDate)}
          maxDate={moment(serverReference.constants.appointment.maxDate)}
          onEnter={submit}
          onEsc={cancel}
        />,
        type: "confirmOrCancelButton",
        fullWidth: true,
        initialValue: state.departureLocal,
        submitChange: (departureLocal: moment.Moment) => {
          const diff = departureLocal.diff(state.departureLocal, "minutes");
          const arrivalLocal = state.arrivalLocal.clone().add(diff, "minutes");
          return window.CoreApi.Train.UpdateTrain({
            ...getUpdateTrainDetails({ ...state, departureLocal, arrivalLocal }),
          })
        }
      }
    },
    right: {
      key: "arrivalLocal",
      render: () => <FormatArrivalTime departure={state.departureLocal} arrival={state.arrivalLocal} />,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<moment.Moment>) => <DateTimePicker
          value={value}
          onChange={v => onChange(v)}
          validatorKey="arrivalDate"
          minDate={state.departureLocal.clone().add(serverReference.constants.flight.maxArrivalBeforeDepartureHours, "hours")}
          maxDate={state.departureLocal.clone().add(serverReference.constants.flight.maxArrivalAfterDepartureHours, "hours")}
          onEnter={submit}
          onEsc={cancel}
        />,
        type: "confirmOrCancelButton",
        fullWidth: true,
        initialValue: state.arrivalLocal,
        submitChange: (arrivalLocal: moment.Moment) => window.CoreApi.Train.UpdateTrain({
          ...getUpdateTrainDetails({ ...state, arrivalLocal }),
        }),
      }
    }
  }, {
    key: "station",
    label: RESX.Train.Station,
    left: {
      ...fieldMetaData.departureStation.fields(),
      key: "departureStation",
      render: () => <SmallDetails
        label={state.departureStation.name}
        subLabel={state.departureStation.address}
      />,
      inPageEdit: {
        render: ({ onChange }: RenderEditOptions<GooglePlaceState>) => <GooglePlaceInput
          calledFrom="train"
          placeType={"trainStation"}
          focusOnMount
          onPlaceSelected={(place) => onChange(place, true)}
        />,
        fullWidth: true,
        type: "custom",
        initialValue: state.departureStation,
        submitChange: (departureStation: GooglePlaceState) => window.CoreApi.Train.UpdateTrain({
          ...getUpdateTrainDetails({
            ...state,
            departureStation
          }),
        }),
      }
    },
    right: {
      ...fieldMetaData.arrivalStation.fields(),
      key: "arrivalStation",
      render: () => <SmallDetails
        label={state.arrivalStation.name}
        subLabel={state.arrivalStation.address}
      />,
      inPageEdit: {
        render: ({ onChange }: RenderEditOptions<GooglePlaceState>) => <GooglePlaceInput
          calledFrom="train"
          placeType={"trainStation"}
          focusOnMount
          onPlaceSelected={(place) => onChange(place, true)}
        />,
        fullWidth: true,
        type: "custom",
        initialValue: state.arrivalStation,
        submitChange: (arrivalStation: GooglePlaceState) => window.CoreApi.Train.UpdateTrain({
          ...getUpdateTrainDetails({
            ...state,
            arrivalStation,
          }),
        }),
      }
    }
  }, {
    key: "platform",
    label: RESX.Train.Platform,
    left: {
      ...fieldMetaData.departurePlatform.fields(),
      key: "departurePlatform",
      render: () => <div>{state.departurePlatform}</div>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
          focusOnMount
          width={60}
          maxLength={serverReference.constants.flight.maxTerminalLength}
          value={value}
          onChange={(v) => { onChange(v); }}
          onEnter={() => submit()}
          onEsc={() => cancel()}
        />,
        fullWidth: false,
        type: "confirmOrCancelButton",
        initialValue: state.departurePlatform ?? "",
        submitChange: (departurePlatform: string) => window.CoreApi.Train.UpdateTrain({
          ...getUpdateTrainDetails({ ...state, departurePlatform }),
        }),
      }
    },
    right: {
      ...fieldMetaData.arrivalPlatform.fields(),
      key: "arrivalPlatform",
      render: () => <div>{state.arrivalPlatform}</div>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
          focusOnMount
          width={60}
          maxLength={serverReference.constants.train.maxPlatformLength}
          value={value}
          onChange={(v) => { onChange(v); }}
          onEnter={() => submit()}
          onEsc={() => cancel()}
        />,
        fullWidth: false,
        type: "confirmOrCancelButton",
        initialValue: state.arrivalPlatform ?? "",
        submitChange: (arrivalPlatform: string) => window.CoreApi.Train.UpdateTrain({
          ...getUpdateTrainDetails({ ...state, arrivalPlatform }),
        }),
      }
    }
  }, {
    key: "filesEmpty",
    label: RESX.File.Files,
    hidden: state.files.length > 0,
    render: () => <TrainFileUpload
      trainId={state.trainId}
      onUploadComplete={onFilesChanged}
    />
  }, {
    key: "files",
    label: RESX.File.Files,
    hidden: state.files.length === 0,
    render: () => <FileList editDisabled files={state.files} onAddFilesClick={() => { }} />,
    inPageEdit: {
      render: ({ cancel }) => <ManageTrainPassengerFilesModal
        trainId={state.trainId}
        isOpen
        onClose={cancel}
        canEdit={site.permissions.train.canCreate}
        files={state.files}
        onFilesChanged={async () => {
          await onFilesChanged();
        }}
      />,
      type: "custom",
      fullWidth: true,
      initialValue: state.files,
      submitChange: () => emptySubmitChangeFunc(),

    }
  }]
}];
